import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/feature/AdminLayout';
import AdminHeader from '@/components/feature/AdminHeader';
import LoadingState from '@/components/base/LoadingState';
import ModuleGate from '@/components/base/ModuleGate';
import { requireAuth } from '@/middleware/authGuard';
import { supabase } from '@/lib/supabaseClient';
import { getTenantId } from '@/lib/getTenant';
import { DEFAULT_AI_SETTINGS, type AiSettings } from './types';
import IdentityTab from './components/IdentityTab';
import BusinessKnowledgeTab from './components/BusinessKnowledgeTab';
import LeadCaptureTab from './components/LeadCaptureTab';
import GoalsTab from './components/GoalsTab';
import PreviewTab from './components/PreviewTab';
import AiStatusSidebar from './components/AiStatusSidebar';

/* ─── Tab config ─────────────────────────────────────────────── */
type TabId = 'identity' | 'knowledge' | 'lead-capture' | 'goals' | 'preview';

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: 'identity',     label: 'Identity',          icon: 'ri-user-star-line' },
  { id: 'knowledge',    label: 'Business Knowledge', icon: 'ri-book-open-line' },
  { id: 'lead-capture', label: 'Lead Capture',       icon: 'ri-user-received-line' },
  { id: 'goals',        label: 'Goals',              icon: 'ri-flag-line' },
  { id: 'preview',      label: 'Preview',            icon: 'ri-eye-line' },
];

/* ─── Page ──────────────────────────────────────────────────── */
export default function AiAssistantSettingsPage() {
  const navigate = useNavigate();
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [rowId, setRowId]       = useState<string | null>(null);
  const [form, setForm]         = useState<AiSettings>(DEFAULT_AI_SETTINGS);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('identity');

  /* ─── Fetch ── */
  const fetchSettings = useCallback(async (tid: string) => {
    const { data } = await supabase
      .from('tenant_ai_settings')
      .select('*')
      .eq('tenant_id', tid)
      .maybeSingle();

    if (data) {
      setRowId(data.id);
      setForm({
        id: data.id,
        assistant_enabled:        data.assistant_enabled        ?? false,
        assistant_name:           data.assistant_name           ?? 'Sales Assistant',
        language:                 data.language                 ?? 'en',
        tone:                     data.tone                     ?? 'professional',
        business_context:         data.business_context         ?? '',
        services_context:         data.services_context         ?? '',
        pricing_context:          data.pricing_context          ?? '',
        faq_context:              data.faq_context              ?? '',
        lead_capture_enabled:     data.lead_capture_enabled     ?? true,
        appointment_goal_enabled: data.appointment_goal_enabled ?? false,
        whatsapp_handoff_enabled: data.whatsapp_handoff_enabled ?? false,
      });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const init = async () => {
      const isAuth = await requireAuth();
      if (!isAuth) { navigate('/login'); return; }
      const tid = await getTenantId();
      setTenantId(tid);
      if (tid) fetchSettings(tid);
      else setLoading(false);
    };
    init();
  }, [navigate, fetchSettings]);

  /* ─── Save ── */
  const handleSave = async () => {
    if (!tenantId) return;
    setSaving(true);
    setSaved(false);
    setSaveError(null);

    const now = new Date().toISOString();
    const payload = {
      tenant_id:                tenantId,
      assistant_enabled:        form.assistant_enabled,
      assistant_name:           form.assistant_name,
      language:                 form.language,
      tone:                     form.tone,
      business_context:         form.business_context || null,
      services_context:         form.services_context || null,
      pricing_context:          form.pricing_context  || null,
      faq_context:              form.faq_context      || null,
      lead_capture_enabled:     form.lead_capture_enabled,
      appointment_goal_enabled: form.appointment_goal_enabled,
      whatsapp_handoff_enabled: form.whatsapp_handoff_enabled,
      updated_at:               now,
    };

    if (rowId) {
      const { error } = await supabase
        .from('tenant_ai_settings')
        .update(payload)
        .eq('id', rowId);
      if (error) {
        setSaveError(error.message);
        setSaving(false);
        return;
      }
    } else {
      const { data, error } = await supabase
        .from('tenant_ai_settings')
        .insert({ ...payload, created_at: now })
        .select('id')
        .maybeSingle();
      if (error || !data) {
        setSaveError(error?.message ?? 'Insert failed');
        setSaving(false);
        return;
      }
      setRowId(data.id);
      setForm((prev) => ({ ...prev, id: data.id }));
    }

    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  /* ─── Field setter ── */
  const set = <K extends keyof AiSettings>(key: K, val: AiSettings[K]) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  /* ─── Render ── */
  return (
    <AdminLayout>
      <AdminHeader
        title="AI Sales Assistant"
        subtitle="Configure how your AI assistant sells for your studio"
      />

      <ModuleGate moduleId="ai_sales_assistant">
        <div className="px-4 md:px-8 py-4 md:py-7">
          {loading ? (
            <LoadingState />
          ) : (
            <div className="grid grid-cols-3 gap-6">

              {/* ─── Left: Tabs + content ─── */}
              <div className="col-span-2 space-y-4">

                {/* Tab bar */}
                <div className="bg-white rounded-xl border border-slate-200 p-1.5 flex gap-1">
                  {TABS.map((tab) => {
                    const active = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                          active
                            ? 'text-white shadow-sm'
                            : 'text-slate-500 hover:text-[#0f172a] hover:bg-slate-50'
                        }`}
                        style={active ? { backgroundColor: '#C8A96E' } : undefined}
                      >
                        <i className={`${tab.icon} text-xs`} />
                        {tab.label}
                      </button>
                    );
                  })}
                </div>

                {/* Tab content */}
                {activeTab === 'identity' && (
                  <IdentityTab form={form} set={set} />
                )}
                {activeTab === 'knowledge' && (
                  <BusinessKnowledgeTab form={form} set={set} />
                )}
                {activeTab === 'lead-capture' && (
                  <LeadCaptureTab form={form} set={set} />
                )}
                {activeTab === 'goals' && (
                  <GoalsTab form={form} set={set} />
                )}
                {activeTab === 'preview' && (
                  <PreviewTab form={form} />
                )}

                {/* Bottom nav between tabs */}
                <div className="flex items-center justify-between pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      const idx = TABS.findIndex((t) => t.id === activeTab);
                      if (idx > 0) setActiveTab(TABS[idx - 1].id);
                    }}
                    disabled={activeTab === TABS[0].id}
                    className="flex items-center gap-1.5 px-4 py-2 text-slate-400 hover:text-[#0f172a] text-xs font-medium transition-colors cursor-pointer disabled:opacity-0"
                  >
                    <i className="ri-arrow-left-line" />
                    Previous
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      const idx = TABS.findIndex((t) => t.id === activeTab);
                      if (idx < TABS.length - 1) setActiveTab(TABS[idx + 1].id);
                    }}
                    disabled={activeTab === TABS[TABS.length - 1].id}
                    className="flex items-center gap-1.5 px-4 py-2 text-[#C8A96E] hover:text-[#b8965a] text-xs font-medium transition-colors cursor-pointer disabled:opacity-0"
                  >
                    Next
                    <i className="ri-arrow-right-line" />
                  </button>
                </div>
              </div>

              {/* ─── Right: sidebar ─── */}
              <div>
                <AiStatusSidebar
                  form={form}
                  saved={saved}
                  saving={saving}
                  saveError={saveError}
                  onSave={handleSave}
                />
              </div>
            </div>
          )}
        </div>
      </ModuleGate>
    </AdminLayout>
  );
}